<template>
  <v-form v-if="ticket && schema" ref="formTicket" lazy-validation>
    <v-row v-if="isActionCreate">
      <v-col cols="12" sm="6">
        <v-select
          v-model="documentType"
          label="Tipo de documento"
          :items="['DNI', 'Carnet de extranjería', 'RUC', 'Pasaporte']"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="documentNumber"
          label="Número de documento"
          append-icon="fa fa-search"
          class="imput-icon"
          hint="Presiona enter para autocompletar"
          :loading="loadingSearchContact"
          @keyup.enter="onSearchContact"
        ></v-text-field>
      </v-col>
    </v-row>

    <VFormBase :col="12" :value="ticket" :schema="schema" :row="{ noGutters: false }" />

    <v-row>
      <v-col cols="12" sm="6">
        <v-autocomplete
          v-model="origin"
          placeholder="Seleccione"
          :rules="[$rules.REQUIRED]"
          :items="getOrigins"
          item-value="name"
          item-text="name"
          label="Origen"
        />
      </v-col>
    </v-row>
    <v-row v-if="isActionCreate && getActiveProducts">
      <v-col cols="12" class="d-flex py-5">
        <p class="font-weight-bold text-body my-auto w100">Productos relacionados</p>
        <div class="text-right">
          <v-tooltip bottom>
            <template #activator="{ on }">
              <v-btn icon small v-on="on" @click="onAddItemProduct">
                <v-icon small> fas fa-plus </v-icon>
              </v-btn>
            </template>
            <span>Agregar producto</span>
          </v-tooltip>
        </div>
      </v-col>
      <v-col cols="12">
        <ItemsProducts :products.sync="products" :discount.sync="discount" action="create" />
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="isActionCreate" cols="12">
        <v-alert dense text type="info">
          Recuerda: Si creas una oportunidad con el <strong>número de documento</strong>, se creará un contacto o de
          existir ya entre tus contactos se vinculará y quedará registrado en el historial de este contacto.
        </v-alert>
      </v-col>
      <v-col v-if="responseMessage" cols="12">
        <v-alert dense text :type="responseType">
          {{ responseMessage }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="action === 'create'" cols="12">
        <v-btn color="primary" :loading="loading" rounded @click="onCreate"> Guardar oportunidad </v-btn>
      </v-col>
      <v-col v-else cols="12">
        <v-btn small rounded color="primary" :loading="loading" @click="onUpdate"> Guardar </v-btn>
        <v-btn small rounded @click="cancelUpdate"> Cancelar </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
<script>
import VFormBase from "vuetify-form-base";
import { mapGetters, mapActions } from "vuex";
import { getCountryList, getCityList } from "@/helpers";
import getModel from "@/helpers/get_model";
import ItemsProducts from "@/components/items/Products";

export default {
  name: "TicketForm",
  components: { VFormBase, ItemsProducts },
  props: {
    action: {
      type: String,
      default: "create",
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      ticket: null,
      products: [],
      discount: 0,
      documentType: "DNI",
      documentNumber: "",
      origin: "",
      isContactFound: false,
      loadingSearchContact: false,
      schema: null,
      loading: false,
      countries: null,
      agentSelected: null,
      countrySelected: null,
      responseMessage: null,
      responseType: null,
    };
  },
  computed: {
    ...mapGetters([
      "getAgent",
      "getTeam",
      "getAgentsToArray",
      "getTicket",
      "getSchemaTicketForm",
      "getProjects",
      "getActiveProducts",
      "getActiveCustomOrigins",
      "getBusiness",
      "getOrigins",
    ]),
    isActionCreate() {
      return this.action === "create";
    },
  },
  watch: {
    documentNumber(value, oldValue) {
      if (value !== oldValue && this.isContactFound) {
        this.resetFormTicket();
        this.isContactFound = false;
      }
    },
    async getTicket(value) {
      if (value && this.action === "update") {
        this.ticket = getModel(this.schema, value);
      }
    },
    async "ticket.country"(value) {
      if (!value) return;

      if (!this.countries) await this.loadCountries();

      this.countrySelected = this.countries.find((c) => {
        return c.name == value;
      });
      if (this.countrySelected) {
        this.cities = await getCityList(this.countrySelected.alpha2code);
        this.schema.city.items = this.cities;
      }
    },
    async "ticket.agent"(value) {
      if (!value || !value.length) return;

      let agent = this.getAgentsToArray.find((c) => {
        return c.full_name == value;
      });
      if (agent) {
        this.agentSelected = {
          email: agent.email,
          full_name: agent.full_name,
          uid: agent.uid,
        };
      }
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...mapActions([
      "getContactByDocumentNumber",
      "createTicket",
      "getAllAgents",
      "getAllProducts",
      "updateTicket",
      "getTeamById",
      "getTeamSchema",
      "getAllProjects",
      "getAllOrigins",
    ]),
    async init() {
      if (!this.getSchemaTicketForm) await this.getTeamSchema({ id: "ticket_form" });
      if (!this.getOrigins) await this.getAllOrigins();

      const isUpdate = this.action === "update";

      this.schema = this.getFormSchemaUpdated(this.getSchemaTicketForm, isUpdate);
      this.ticket = getModel(this.schema, isUpdate ? { ...this.getTicket } : {});

      if (this.schema.country) this.loadCountries();
      if (this.schema.project) this.loadProjects(isUpdate);

      if (!isUpdate) {
        await this.loadAgents();
        await this.loadOrigins();
        await this.loadProducts();
      }

      this.$emit("onLoaded");
    },
    async onSearchContact() {
      try {
        if (this.loadingSearchContact) return;

        this.loadingSearchContact = true;

        const contact = await this.getContactByDocumentNumber({ documentNumber: this.documentNumber });
        if (contact) {
          delete contact.ticket_name;
          delete contact.commet;
          this.ticket = getModel(this.schema, { ...contact });
          this.isContactFound = true;
        } else {
          this.resetFormTicket();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingSearchContact = false;
      }
    },
    onAddItemProduct() {
      this.products.push({
        id: "",
        name: "",
        price: 0,
        quantity: 1,
      });
    },
    getFormSchemaUpdated(schema, update = false) {
      let schemaUpdated = {};
      let schemaField;
      let formSchema = schema.map((fs) => ({ ...fs }));

      for (let fieldSelected of formSchema) {
        schemaField = null;

        if (fieldSelected.rules)
          fieldSelected.rules = fieldSelected.rules.map((rule) => this.$rules[rule.toUpperCase()]);

        if (update && fieldSelected.form_update) {
          fieldSelected.flex = { xs: 12 };
          schemaField = fieldSelected;
        } else if (!update) {
          schemaField = fieldSelected;
        }

        if (
          schemaField &&
          schemaField.type != "hidden" &&
          !schemaField.show_only_segments &&
          !["document_number", "document_type", "quotation_code"].includes(fieldSelected.id)
        )
          schemaUpdated[fieldSelected.id] = schemaField;
      }

      return schemaUpdated;
    },
    async loadAgents() {
      if (!this.getAgentsToArray) await this.getAllAgents();

      this.schema.agent.items = this.getAgentsToArray.map((a) => a.full_name);
      this.ticket.agent = this.getAgent.full_name;
    },
    async loadOrigins() {
      if (!this.schema.origin) return;

      if (!this.getActiveCustomOrigins) await this.getAllOrigins();

      this.schema.origin.items = this.getActiveCustomOrigins.map((o) => o.name);
    },
    async loadCountries() {
      this.countries = await getCountryList();
      this.schema.country.items = this.countries.map((c) => c.name);
    },
    async loadProjects(isUpdate) {
      if (!this.getProjects) await this.getAllProjects();

      this.schema.project.items = this.getProjects.map((p) => p.id);

      if (!isUpdate && this.getAgent.role === this.$roles.ENTREPRENEUR) this.ticket.project = this.getAgent.team_id;
    },
    async loadProducts() {
      await this.getAllProducts();
    },
    addExtraData() {
      if (this.agentSelected) {
        this.ticket.agent_id = this.agentSelected.uid;
        this.ticket.agent = this.agentSelected;
      }
      if (this.countrySelected) {
        this.ticket.country = this.countrySelected.name;
        this.ticket.country_alpha3code = this.countrySelected.alpha3code;
      }
    },
    resetFormTicket() {
      this.ticket = getModel(this.schema, {});
      this.$refs.formTicket.resetValidation();
    },
    async onCreate() {
      try {
        this.loading = true;
        this.responseType = null;
        this.responseMessage = null;

        if (!this.$refs.formTicket.validate())
          throw new Error("El formulario contiene datos inválidos, por favor verifique y vuelva a intentar");

        if (!this.ticket.email && !this.documentNumber)
          throw new Error(
            "Debe registar la oportunidad al menos con correo electrónico o con número de documento (DNI)",
          );

        this.addExtraData();

        const response = await this.createTicket({
          ticket: {
            ...this.ticket,
            document_type: this.documentType,
            document_number: this.documentNumber,
            origin: this.origin,
            products: this.products,
            discount: this.discount,
            currency: this.getBusiness.currency || this.$currency,
          },
        });

        this.$router.replace({ name: "ticket_detail", params: { id: response.data.id } });

        this.responseType = response.status;
        this.responseMessage = response.message;
      } catch (error) {
        this.responseType = error.status || "error";
        this.responseMessage = error.message || "Ocurrió un error al guardar la oportunidad";
      } finally {
        this.loading = false;
      }
    },
    async onUpdate() {
      try {
        this.loading = true;
        this.responseType = null;
        this.responseMessage = null;

        if (!this.$refs.formTicket.validate()) return;

        this.addExtraData();

        const response = await this.updateTicket({ ticket_id: this.getTicket.id, data: this.ticket });

        this.responseType = response.status;
        this.responseMessage = response.message;

        this.cancelUpdate();
      } catch (error) {
        this.responseType = error.status || "error";
        this.responseMessage = error.message || "Ocurrió un error al actualizar la oportunidad";
      } finally {
        this.loading = false;
      }
    },
    cancelUpdate() {
      this.$emit("cancelInformation");
    },
  },
};
</script>
